import { useCallback, useEffect, useRef, useState } from "react";
import { getBinding } from "../../lib/binding";
import type {
  CareTask,
  CareTaskCategory,
  RealtimeEvent,
} from "@shared/types/foyer";

/**
 * In-room service requests ("call staff"). Lets the resident raise a request
 * from the TV and reflects the room's pending calls (open/acknowledged), so the
 * screen can show "staff is on the way". Polls every 15 s while active and
 * re-fetches on a `care_task_changed` realtime event.
 *
 * Reads/writes are screen-token authed (or, in CMS preview, plain fetch to the
 * previewed room).
 */
export function useServiceRequests(
  active: boolean,
  realtimeEvent?: RealtimeEvent | null,
  previewPremiseId?: string,
  previewRoomId?: string
): {
  pending: CareTask[];
  raising: boolean;
  error: string | null;
  raise: (category: CareTaskCategory) => Promise<void>;
} {
  const [pending, setPending] = useState<CareTask[]>([]);
  const [raising, setRaising] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Resolve the (premiseId, roomId, headers) to talk to for this device. */
  const target = useCallback((): {
    premiseId: string;
    roomId: string;
    headers: Record<string, string>;
  } | null => {
    const binding = getBinding();
    if (binding) {
      const headers: Record<string, string> = {};
      if (binding.screenToken) headers["X-Screen-Token"] = binding.screenToken;
      return { premiseId: binding.premiseId, roomId: binding.roomId, headers };
    }
    if (previewPremiseId && previewRoomId) {
      return {
        premiseId: previewPremiseId,
        roomId: previewRoomId,
        headers: {},
      };
    }
    return null;
  }, [previewPremiseId, previewRoomId]);

  const refresh = useCallback(async () => {
    const t = target();
    if (!t) return;
    try {
      const res = await fetch(
        `/api/frontlayer/v1/premise/${t.premiseId}/rooms/${t.roomId}/care/requests`,
        { headers: t.headers }
      );
      if (res.ok) setPending((await res.json()) as CareTask[]);
    } catch {
      // best-effort poll
    }
  }, [target]);

  const raise = useCallback(
    async (category: CareTaskCategory) => {
      const t = target();
      if (!t) return;
      setRaising(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/frontlayer/v1/premise/${t.premiseId}/rooms/${t.roomId}/care/requests`,
          {
            method: "POST",
            headers: { ...t.headers, "content-type": "application/json" },
            body: JSON.stringify({ category }),
          }
        );
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          setError(`HTTP ${res.status} - ${body.slice(0, 120)}`);
        } else {
          await refresh();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setRaising(false);
      }
    },
    [target, refresh]
  );

  useEffect(() => {
    if (!active) return;
    void refresh();
    intervalRef.current = setInterval(refresh, 15_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active, refresh]);

  useEffect(() => {
    if (!realtimeEvent || realtimeEvent.type !== "care_task_changed") return;
    void refresh();
  }, [realtimeEvent, refresh]);

  return { pending, raising, error, raise };
}
